import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { FileText, Package, AlertTriangle } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';
import Pagination from '@/components/Pagination';

const InventoryReport = () => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ totalItems: 0, totalValue: 0, lowStock: 0, outOfStock: 0 });
  const [loading, setLoading] = useState(false);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => { fetchReport(); }, [currentPage, itemsPerPage]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);
      
      const response = await apiClient.get(`/api/inventory/items?${params.toString()}`);
      
      // Handle both array and paginated response
      let itemsData = [];
      let total = 0;
      
      if (Array.isArray(response.data)) {
        itemsData = response.data;
        total = response.data.length;
      } else {
        itemsData = response.data?.data || response.data?.items || [];
        total = response.data?.total || itemsData.length;
      }
      
      setItems(itemsData);
      setTotalItems(total);
      
      // Calculate summary
      const totalValue = itemsData.reduce((sum, item) => {
        if (item.total_value) return sum + (parseFloat(item.total_value) || 0);
        const price = parseFloat(item.unit_price) || 0;
        const qty = parseFloat(item.quantity) || 0;
        return sum + (price * qty);
      }, 0);
      
      const lowStock = itemsData.filter(item => {
        const qty = parseFloat(item.quantity) || 0;
        const minStock = parseFloat(item.minimum_stock) || 0;
        return qty <= minStock && qty > 0;
      }).length;
      
      const outOfStock = itemsData.filter(item => {
        const qty = parseFloat(item.quantity) || 0;
        return qty === 0;
      }).length;
      
      setSummary({ totalItems: total, totalValue, lowStock, outOfStock });
    } catch (error) {
      console.error('Error:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Helper function to safely format numbers
  const formatNumber = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '0.00';
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getItemValue = (item) => {
    // Use total_value if available
    if (item.total_value) return parseFloat(item.total_value) || 0;
    const price = parseFloat(item.unit_price) || 0;
    const qty = parseFloat(item.quantity) || 0;
    return price * qty;
  };

  const getStockStatus = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const minStock = parseFloat(item.minimum_stock) || 0;
    
    if (qty === 0) return { label: 'Out of Stock', class: 'bg-red-100 text-red-800', isLow: true };
    if (qty <= minStock) return { label: 'Low Stock', class: 'bg-yellow-100 text-yellow-800', isLow: true };
    return { label: 'In Stock', class: 'bg-green-100 text-green-800', isLow: false };
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-600" />
            Inventory Report
          </CardTitle>
          <Button onClick={fetchReport} disabled={loading}>
            <FileText className="w-4 h-4 mr-2" />{loading ? 'Loading...' : 'Refresh'}
          </Button>
        </CardHeader>
        <CardContent>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-600">Total Items</p>
              <p className="text-2xl font-bold text-blue-700">{summary.totalItems}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-600">Total Stock Value</p>
              <p className="text-2xl font-bold text-green-700">₹{formatNumber(summary.totalValue)}</p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-600 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" /> Low Stock
              </p>
              <p className="text-2xl font-bold text-yellow-700">{summary.lowStock}</p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-600">Out of Stock</p>
              <p className="text-2xl font-bold text-red-700">{summary.outOfStock}</p>
            </div>
          </div>

          {/* Items Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3">Item Code</th>
                  <th className="text-left p-3">Item Name</th>
                  <th className="text-left p-3">Category</th>
                  <th className="text-left p-3">Unit</th>
                  <th className="text-right p-3">Quantity</th>
                  <th className="text-right p-3">Min Stock</th>
                  <th className="text-right p-3">Unit Price</th>
                  <th className="text-right p-3">Total Value</th>
                  <th className="text-center p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center p-8 text-gray-500">
                      <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No inventory items found</p>
                    </td>
                  </tr>
                ) : (
                  items.map((item, idx) => {
                    const status = getStockStatus(item);
                    return (
                      <tr key={idx} className={`border-b hover:bg-gray-50 ${status.isLow ? 'bg-red-50' : ''}`}>
                        <td className="p-3 font-medium">{item.item_code || 'N/A'}</td>
                        <td className="p-3">{item.item_name || 'N/A'}</td>
                        <td className="p-3">{item.category || 'N/A'}</td>
                        <td className="p-3">{item.unit || 'Piece'}</td>
                        <td className="p-3 text-right">
                          <span className={status.isLow ? 'text-red-600 font-bold' : ''}>
                            {parseFloat(item.quantity) || 0}
                          </span>
                        </td>
                        <td className="p-3 text-right">{parseFloat(item.minimum_stock) || 0}</td>
                        <td className="p-3 text-right">₹{formatNumber(item.unit_price)}</td>
                        <td className="p-3 text-right font-semibold">₹{formatNumber(getItemValue(item))}</td>
                        <td className="p-3 text-center">
                          <span className={`px-2 py-1 text-xs rounded-full ${status.class}`}>
                            {status.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {items.length > 0 && (
                <tfoot className="bg-gray-50">
                  <tr className="font-bold">
                    <td colSpan="7" className="p-3 text-right">Grand Total:</td>
                    <td className="p-3 text-right text-green-700">₹{formatNumber(summary.totalValue)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          
          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(totalItems / itemsPerPage)}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default InventoryReport;
